import { ArrowRight, X } from 'lucide-react'
import { useMemo } from 'react'
import { useLocalStorage } from 'usehooks-ts'

import { type SidebarPromoCardTargetAudience } from 'components/admin/types'
import { ButtonLink } from 'slab'
import { useGetUsersSidebarPromoCardsActive } from 'utils/web_api'

interface SidebarPromoCardProps {
  audience?: string
}

function getAudienceCode (audience: string): string {
  switch (audience) {
    case 'employer':
      return 'ER'
    case 'member':
      return 'EE'
    case 'partner':
      return 'TfB'
    default:
      return 'UnknownContext'
  }
}

export function SidebarPromoCard ({ audience }: SidebarPromoCardProps) {
  if (!audience) {
    return null
  }

  const { data: card, isLoading, error } = useGetUsersSidebarPromoCardsActive(
    { audience: audience as SidebarPromoCardTargetAudience },
    {
      query: {
        enabled: !!audience,
        retry: false,
      },
    }
  )

  const localStorageKey = card ? `sidebar-promo-dismissed-${card.id}` : ''
  const [isDismissed, setIsDismissed] = useLocalStorage(localStorageKey, false)

  const audienceCode = useMemo(() => getAudienceCode(audience), [audience])

  const isExpired = useMemo(() => {
    if (!card?.expires_at) return false
    const expirationDate = new Date(card.expires_at)
    const now = new Date()
    return now > expirationDate
  }, [card?.expires_at])

  const handleDismiss = () => {
    setIsDismissed(true)
    window?.segment?.track(`[${audienceCode}] NavPromo: dismissed card (${card?.title})`)
  }

  const handleCtaClick = () => {
    window?.segment?.track(`[${audienceCode}] NavPromo: clicked cta (${card?.title})`)
  }

  if (isLoading || error || !card || isDismissed || isExpired) {
    return null
  }

  return (
    <div className='px-2 mb-2'>
      <div className='relative bg-white border border-gray-200 rounded-lg p-3 shadow-sm'>
        <button
          onClick={handleDismiss}
          className='absolute top-2 right-2 text-gray-400 hover:text-gray-600 transition-colors'
          aria-label='Dismiss'
        >
          <X size={14} />
        </button>
        <div className='flex flex-col gap-2'>
          <h3 className='heading-sm pr-6'>
            {card.title}
          </h3>
          <p className='body'>
            {card.content}
          </p>
          {card.image_url && (
            <div className='mt-1'>
              <img
                src={card.image_url}
                alt={card.image_alt}
                className='w-full rounded-md'
              />
            </div>
          )}
          {card.cta_href && card.cta_text && (
            <div className='mt-1 flex justify-end'>
              <ButtonLink
                href={card.cta_href}
                variant='tertiary'
                size='small'
                width='auto'
                rightIcon={ArrowRight}
                onClick={handleCtaClick}
              >
                {card.cta_text}
              </ButtonLink>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
